import { Reveal, SectionTag } from "./shared";

const CHAPTERS = [
  {
    n: "01",
    title: "Raw turbine power",
    body: "A brushless motor spinning at up to 110,000 RPM drives a 13-blade aviation aluminum fan, funneling air into a concentrated jet at 33 m/s. Dust, crumbs, water droplets, snow — gone before they know what hit them.",
    img: "/assets/cj-3.jpg",
    alt: "X8 Violent Fan blasting dust in a real workshop",
    caption: "Full send, real workshop",
    wide: true,
  },
  {
    n: "02",
    title: "A jet engine in your palm",
    body: "420 grams. Pocketable. Smaller than the soda can it replaces. An infinitely variable speed dial puts the full range under one thumb, and the 3-level display shows remaining power at a glance.",
    img: "/assets/x8-2.jpg",
    alt: "X8 Violent Fan in one hand showing power display",
    caption: "One-hand command center",
    wide: false,
  },
  {
    n: "03",
    title: "Built for the long haul",
    body: "An 8,000 mAh reservoir keeps it blasting through whole detailing sessions, and USB-C fast charging tops it up from any port, power bank or laptop. No proprietary docks, no dead-weight downtime.",
    img: "/assets/pexels-turbine.jpg",
    alt: "Turbine core detail",
    caption: "8,000 mAh · USB-C fast charge",
    wide: false,
  },
  {
    n: "04",
    title: "Canned air is cancelled",
    body: "One X8 outlives thousands of disposable air cans. No freezing propellant, no chemical huff, no landfill guilt. Recharge over USB-C and blast again — session after session, year after year.",
    img: "/assets/x8-main.jpg",
    alt: "X8 Violent Fan retail box with unit and USB-C cable",
    caption: "In the box: unit, USB-C cable",
    wide: true,
  },
];

const Chapter = ({ c, flip }) => (
  <div className={`grid items-stretch gap-4 lg:grid-cols-12 ${flip ? "" : ""}`}>
    <div className={`flex flex-col justify-between border border-steel bg-carbon p-8 md:p-14 ${c.wide ? "lg:col-span-5" : "lg:col-span-6"} ${flip ? "lg:order-2" : ""}`}>
      <div>
        <span className="text-outline font-display text-7xl font-black md:text-9xl" data-testid={`chapter-${c.n}-number`}>
          {c.n}
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold uppercase leading-tight tracking-tight md:text-4xl" data-testid={`chapter-${c.n}-title`}>
          {c.title}
        </h3>
        <p className="mt-5 max-w-md text-sm leading-relaxed text-smoke md:text-base">{c.body}</p>
      </div>
      <div className="mt-10 hidden items-center gap-3 font-mono text-[10px] tracking-[0.3em] uppercase text-smoke md:flex">
        <span className="h-px w-10 bg-volt" />
        Chapter {c.n} / 04
      </div>
    </div>
    <div className={`group relative overflow-hidden border border-steel ${c.wide ? "lg:col-span-7" : "lg:col-span-6"} ${flip ? "lg:order-1" : ""}`}>
      <img
        src={c.img}
        alt={c.alt}
        loading="lazy"
        className="h-[320px] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 md:h-[480px]"
      />
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between border-t border-white/10 bg-ink/70 px-5 py-3 font-mono text-[10px] tracking-[0.25em] uppercase backdrop-blur-md">
        <span className="text-smoke">{c.caption}</span>
        <span className="text-volt">{c.n}</span>
      </div>
    </div>
  </div>
);

export const Manifesto = () => (
  <section id="chapters" data-testid="manifesto-section" className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-36">
    <Reveal>
      <SectionTag index="01—04" label="The Manifesto" />
      <h2 className="mt-6 max-w-4xl font-display text-3xl font-black uppercase leading-[0.95] tracking-tighter md:text-6xl">
        Four reasons this thing <span className="text-volt">went viral</span>
      </h2>
    </Reveal>
    <div className="mt-14 flex flex-col gap-4 md:mt-20">
      {CHAPTERS.map((c, i) => (
        <Reveal key={c.n} delay={0.05}>
          <Chapter c={c} flip={i % 2 === 1} />
        </Reveal>
      ))}
    </div>
  </section>
);
